import type { StatusIconName } from '../../components/ui/StatusIcon';
import type { DelayReason, ShipmentDelay } from '../../api/types';
import { DnmRequestFailure } from '../../api/dnmFailure';
import { formatAcceptedAt } from './driverAcceptance';

/** Longest Other text the API accepts (Contract dnm-work#150). */
export const DELAY_TEXT_MAX = 120;

/** The one code → label mapping for the Record-delay sheet, the overview row and the public page. */
export const DELAY_REASONS: readonly { code: DelayReason; label: string; icon: StatusIconName }[] = [
  { code: 'TRAFFIC', label: 'Traffic', icon: 'schedule' },
  { code: 'BREAKDOWN', label: 'Breakdown', icon: 'local_shipping' },
  { code: 'WAITING_AT_PICKUP', label: 'Waiting at pickup', icon: 'hourglass' },
  { code: 'WAITING_AT_UNLOADING', label: 'Waiting at unloading', icon: 'history' },
  { code: 'OTHER', label: 'Other', icon: 'edit' },
];

/** Other shows the typed text; unknown or newer codes still read as a reason, never as a raw code. */
export function delayReasonLabel(delay: Pick<ShipmentDelay, 'reason' | 'reasonText'>): string {
  if (delay.reason === 'OTHER' && delay.reasonText) return delay.reasonText;
  return DELAY_REASONS.find((r) => r.code === delay.reason)?.label ?? 'Other reason';
}

/** DD Mon YYYY, HH:mm IST (UX principle 4). */
export function formatIst(iso: string): string {
  return `${formatAcceptedAt(iso)} IST`;
}

export const DELAY_COPY = {
  DELAY_REASON_TEXT_REQUIRED: 'Add a short reason for Other.',
  DELAY_TIME_IN_PAST: 'Pick a time later than now.',
  SHIPMENT_NOT_IN_TRANSIT: 'Delays can be recorded only while the truck is on the way.',
} as const;

export type DelayProblem = keyof typeof DELAY_COPY;

/** The checks the API makes, run before the call so the inline message shows at once (the server stays the authority). */
export function delayProblem(
  reason: DelayReason | null,
  text: string,
  newExpectedAt: string | null,
  now: number = Date.now(),
): DelayProblem | null {
  if (!reason) return null;
  const trimmed = text.trim();
  if (reason === 'OTHER' && (trimmed.length === 0 || trimmed.length > DELAY_TEXT_MAX)) return 'DELAY_REASON_TEXT_REQUIRED';
  if (newExpectedAt && Date.parse(newExpectedAt) <= now) return 'DELAY_TIME_IN_PAST';
  return null;
}

/** Copy for the API's delay refusals; null for any other failure (the caller uses actionFailureMessage). */
export function delayFailureCopy(error: unknown): string | null {
  if (!(error instanceof DnmRequestFailure) || !error.code) return null;
  return error.code in DELAY_COPY ? DELAY_COPY[error.code as DelayProblem] : null;
}
